import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { Home } from 'lucide-react';

interface RoomTypeData {
  roomType: string;
  roomCount: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}

interface IssuesByRoomChartProps {
  data: RoomTypeData[] | null;
  isLoading?: boolean;
}

export const IssuesByRoomChart: React.FC<IssuesByRoomChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Home className="h-5 w-5" />
            Issues by Room Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[350px]" />
        </CardContent>
      </Card>
    );
  }

  const chartData = data?.map(item => ({
    name: item.roomType,
    High: item.high,
    Medium: item.medium,
    Low: item.low,
    Rooms: item.roomCount,
  })) ?? [];

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-foreground flex items-center gap-2">
          <Home className="h-5 w-5" />
          Issues by Room Type
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis 
                type="category" 
                dataKey="name" 
                stroke="hsl(var(--muted-foreground))" 
                fontSize={12}
                width={100}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
                formatter={(value: number, name: string) => [value, name]}
              />
              <Legend />
              <Bar dataKey="High" stackId="issues" fill="hsl(var(--destructive))" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Medium" stackId="issues" fill="hsl(25, 95%, 53%)" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Low" stackId="issues" fill="hsl(142, 76%, 36%)" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
